import { getCoreRowModel, getExpandedRowModel } from "@tanstack/react-table"
import type { ExpandedState } from "@tanstack/react-table"
import type { FC } from "react"
import { useState } from "react"

import { useTransferFormProductColumns } from "../../hooks/useTransferProductColumns"
import type { TransferFormProductInventory } from "../../types"

import { TransferFormProductRowDetails } from "./TransferFormProductRowDetails"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { WarehouseArea } from "@/graphql/codegen/graphql"

interface TransferFormProductTableProps {
  data: TransferFormProductInventory[]
  targetWarehouseArea?: WarehouseArea
  readOnly?: boolean
  onProductRemove: (productId: number) => void
  onPalletsSelect: (productId: number) => void
  onPalletOverride: (productId: number, palletReferenceNumber: number) => void
  onPalletRemove: (productId: number, palletReferenceNumber: number) => void
  onLotRemove: (productId: number, rawInventoryId: string) => void
}

export const TransferFormProductTable: FC<TransferFormProductTableProps> = ({
  data,
  targetWarehouseArea,
  readOnly = false,
  onProductRemove,
  onPalletsSelect,
  onPalletOverride,
  onPalletRemove,
  onLotRemove,
}) => {
  const [expanded, setExpanded] = useState<ExpandedState>({})
  const columns = useTransferFormProductColumns({ onProductRemove, onPalletsSelect, readOnly })

  return (
    <AerosBaseTable
      features={{
        table: {
          stickyHeader: true,
        },
        tanstackOptions: {
          data,
          columns,
          getRowId: (row) => String(row.product.productId),
          state: {
            expanded,
          },
          getCoreRowModel: getCoreRowModel(),
          getExpandedRowModel: getExpandedRowModel(),
          onExpandedChange: setExpanded,
          getRowCanExpand: () => true,
          renderExpandedContent: (row) => (
            <TransferFormProductRowDetails
              pallets={row.original.pallets}
              uomName={row.original.product.packageUsWeightUomDetails?.name ?? ""}
              targetWarehouseArea={targetWarehouseArea}
              readOnly={readOnly}
              onPalletOverride={(palletReferenceNumber) =>
                onPalletOverride(row.original.product.productId, palletReferenceNumber)
              }
              onPalletRemove={(palletReferenceNumber) =>
                onPalletRemove(row.original.product.productId, palletReferenceNumber)
              }
              onLotRemove={(rawInventoryId) => onLotRemove(row.original.product.productId, rawInventoryId)}
            />
          ),
        },
      }}
      slotProps={{
        bodyCell: { className: "align-top" },
        bodyCellExpanded: { className: "bg-primary-100" },
      }}
      noDataMessage='No products added yet'
    />
  )
}
